import { Link } from 'wouter';
import type { Bucket, Card } from '../lib/api';
import { ageOf, until, whenLocal } from '../lib/time';
import { cardLook, isConsequential, who, portalWait } from '../lib/cards';
import { cardHref } from './Rail';
import './cardlist.css';

/**
 * The queue, oldest first.
 *
 * A list of decisions is worked from the one that has been waiting longest.
 * Newest-first buries this morning's card under the eleven raised since, which
 * is how a card sits for three days while the queue looks busy.
 */
export function CardList({ title, cards, selectedId, loading, error, bucket, onSlim }: {
  title: string;
  cards: Card[];
  selectedId: number | null;
  loading: boolean;
  error: string | null;
  // Which bucket these rows belong to, so a row opens the card WITHOUT losing
  // the bucket — see cardHref in Rail.tsx.
  bucket: Bucket;
  /** With a card open on a desktop (fix-74): fold the queue back to its slim rail. */
  onSlim?: () => void;
}) {
  return (
    <section className="list" aria-label={`Cards: ${title}`}>
      <div className="list-head">
        <h1>{title}</h1>
        <span className="mono list-order">oldest first</span>
        {onSlim && (
          <button type="button" className="list-slim" onClick={onSlim} aria-label="Fold the queue (Q)" title="Fold the queue (Q)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
        )}
      </div>

      <div className="list-body">
        {loading && <p className="list-empty">Reading the queue…</p>}
        {error && !loading && <p className="list-empty list-error">{error}</p>}
        {!loading && !error && cards.length === 0 && (
          <p className="list-empty">Nothing here. That is the good version of this screen.</p>
        )}
        {cards.map((card) => (
          <CardRow key={card.id} card={card} selected={card.id === selectedId} bucket={bucket} />
        ))}
      </div>
    </section>
  );
}

function CardRow({ card, selected, bucket }: { card: Card; selected: boolean; bucket: Bucket }) {
  const look = cardLook(card);
  const veto = card.veto_deadline && card.status === 'pending';
  const wait = portalWait(card);

  return (
    <Link
      href={cardHref(bucket, card.id)}
      className={`card-row ${selected ? 'is-selected' : ''}`}
      aria-current={selected ? 'true' : undefined}
    >
      <div className="card-row-top">
        <span className={`badge mono badge-${look.tone}`}>{look.label}</span>
        <span className="card-row-who">{who(card)}</span>
        <span className="mono card-row-age">{ageOf(card.created_at)}</span>
      </div>

      <div className="card-row-title">{card.summary || card.title}</div>

      {wait ? (
        // #217: approved, and the portal could not take it yet (fix-75).
        <div className="mono card-row-note note-amber">Waiting for the portal · tries until {whenLocal(wait.until)}</div>
      ) : isConsequential(card) ? (
        // Said on the card, not in a footnote. T31 suspends an account on
        // send, and the operator should not have to remember which ones those
        // are.
        <div className="mono card-row-note note-rust">Consequential · always held for you</div>
      ) : veto ? (
        <div className="mono card-row-note note-amber">
          Sends by itself in {until(card.sends_at ?? card.veto_deadline)}
          {card.quiet_hours ? ' (held for 08:00 by quiet hours)' : ''} unless you stop it
        </div>
      ) : card.confidence !== null ? (
        <div className="card-row-meter">
          <span className="meter"><span className="meter-fill" style={{ width: `${Math.round(card.confidence * 100)}%` }} /></span>
          <span className="mono card-row-note">
            {card.confidence.toFixed(2)} · {card.evidence.length} check{card.evidence.length === 1 ? '' : 's'}
          </span>
        </div>
      ) : card.evidence.length > 0 ? (
        <div className="mono card-row-note">{card.evidence.length} checks</div>
      ) : null}
    </Link>
  );
}

/**
 * The queue folded to a slim rail while a card is open on a desktop (fix-74): the bucket's count, where this card is
 * in it, previous and next (K and J), and the button that brings the queue back (Q).
 */
export function QueueRail({ title, cards, selectedId, onStep, onShow }: {
  title: string;
  cards: Card[];
  selectedId: number | null;
  onStep: (dir: 1 | -1) => void;
  onShow: () => void;
}) {
  const at = selectedId === null ? -1 : cards.findIndex((c) => c.id === selectedId);
  const arrow = (d: string) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d={d} /></svg>
  );
  return (
    <nav className="qrail" aria-label={`Queue: ${title}`}>
      <button type="button" className="qrail-btn qrail-show" onClick={onShow} aria-label="Show the queue (Q)" data-tip="Show the queue · Q">
        {arrow('M9 6l6 6-6 6')}
      </button>
      <span className="mono qrail-count" title={title}>{cards.length}</span>
      <span className="mono qrail-pos">{at >= 0 ? `${at + 1}/${cards.length}` : '–'}</span>
      <button type="button" className="qrail-btn" onClick={() => onStep(-1)} disabled={at <= 0}
        aria-label="Previous card (K)" data-tip="Previous · K">{arrow('M6 15l6-6 6 6')}</button>
      <button type="button" className="qrail-btn" onClick={() => onStep(1)} disabled={at < 0 || at >= cards.length - 1}
        aria-label="Next card (J)" data-tip="Next · J">{arrow('M6 9l6 6 6-6')}</button>
      <span className="qrail-title mono" aria-hidden="true">{title}</span>
    </nav>
  );
}
